/**
 * AI Costs — the spend ledger for the rewired Quill engine (P3-C redesign).
 *
 * Two filters drive the whole page:
 *   1. Time range: the shared TimeRangeFilter over this screen's own vocabulary
 *      — 1D · 7D · 1M · 3M · QTD · YTD · 1Y · Custom. QTD/YTD/1Y are period
 *      starts, not calendar-day lookbacks, so the screen keeps `computeRange`
 *      (costTimeRange.ts) rather than the shared day resolver; the results are
 *      passed as from&to to every cost endpoint. Default 1M.
 *   2. Key filter: a multi-select of the cost centers (the API keys). Any
 *      selection passes `cost_centers` (comma list) to every endpoint and scopes
 *      the entire page. "All" (nothing selected) is the default.
 *
 * Sections: totals metric cards (total / xAI / Anthropic), the per-key spend
 * breakdown (the headline — cost for each key with a proportion bar), the daily
 * trend chart, the per-pipeline table, top members, and a per-run drill-down.
 * Every section renders cleanly at zero data, loads independently
 * (Promise.allSettled), and surfaces its own error + Retry (an outage is never
 * silently rendered as "no data").
 *
 * Fetch concurrency: each endpoint has a monotonic request-sequence token
 * (last-request-wins) so a slow earlier response can never overwrite a newer
 * one when the range / key / provider filter changes rapidly (D6).
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Alert,
  Button,
  Chip,
  Link as MuiLink,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import PaidIcon from '@mui/icons-material/Paid';
import { useNavigate } from 'react-router-dom';
import { useQuillHealth } from '../QuillHealthContext';
import MetricCard from '../../common/MetricCard';
import DataTable, { Column } from '../../common/DataTable';
import EmptyState from '../../common/EmptyState';
import { colors, typography } from '../../../theme/designTokens';
import {
  quillApi,
  CostSummary,
  CostDailyPoint,
  CostPipelineRow,
  CostMemberRow,
} from '../../../services/quillAdminService';
import CostTrendChart from './CostTrendChart';
import CostByKeyBreakdown from './CostByKeyBreakdown';
import CostRunDrilldown from './CostRunDrilldown';
import TimeRangeFilter from '../../common/TimeRangeFilter';
import useTimeRangeParam from '../../common/useTimeRangeParam';
import { num, formatCost, providerLabel } from './costFormat';
import { COST_PRESETS, TimePreset, computeRange } from './costTimeRange';

// The seven cost centers (API keys) the engine meters against. Used as an
// ORDERING HINT only — the live chip list is derived by unioning this with the
// cost centers actually present in the summary response (D10), so a newly
// metered center shows up without a code change.
const COST_CENTERS = [
  'conversation',
  'memory-extraction',
  'social-ingestion',
  'photo-pipeline',
  'admin-tools',
  'prosody',
  'speech',
];

// Re-exported for existing consumers/tests that imported these from the page.
export { num, formatCost };

/** Small inline error+Retry block for a single section (reuses MUI Alert). */
const SectionError: React.FC<{ message: string; onRetry: () => void }> = ({ message, onRetry }) => (
  <Alert
    severity="error"
    action={
      <Button color="inherit" size="small" onClick={onRetry} sx={{ textTransform: 'none' }}>
        Retry
      </Button>
    }
  >
    {message}
  </Alert>
);

const AICostsPage: React.FC = () => {
  const { status } = useQuillHealth();
  const navigate = useNavigate();
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // ── Time filter ──────────────────────────────────────────────────────
  const [timeRange, setTimeRange] = useTimeRangeParam<TimePreset>(
    { type: '1M' },
    { presets: COST_PRESETS }
  );

  const range = useMemo(
    () =>
      timeRange.type === 'custom'
        ? { from: timeRange.from, to: timeRange.to }
        : computeRange(timeRange.type),
    [timeRange]
  );

  // ── Key filter (cost centers) ────────────────────────────────────────
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const costCentersParam = useMemo(
    () => (selectedKeys.size ? Array.from(selectedKeys).join(',') : undefined),
    [selectedKeys]
  );
  const toggleKey = (key: string) =>
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const clearKeys = () => setSelectedKeys(new Set());

  // ── Provider filter (scopes the daily trend + pipeline table) ────────
  const [providerFilter, setProviderFilter] = useState<string>('');

  const [summary, setSummary] = useState<CostSummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const [daily, setDaily] = useState<CostDailyPoint[]>([]);
  const [dailyLoading, setDailyLoading] = useState(true);
  const [dailyError, setDailyError] = useState<string | null>(null);
  const [pipelines, setPipelines] = useState<CostPipelineRow[]>([]);
  const [pipelinesLoading, setPipelinesLoading] = useState(true);
  const [pipelinesError, setPipelinesError] = useState<string | null>(null);
  const [members, setMembers] = useState<CostMemberRow[]>([]);
  const [membersLoading, setMembersLoading] = useState(true);
  const [membersError, setMembersError] = useState<string | null>(null);

  // Per-endpoint request-sequence tokens (last-request-wins). Each loader bumps
  // its own token and only commits state if the token is still current, so a
  // stale in-flight response is discarded rather than clobbering fresh data.
  const summarySeq = useRef(0);
  const dailySeq = useRef(0);
  const pipelinesSeq = useRef(0);
  const membersSeq = useRef(0);

  const loadSummary = useCallback(async (from: string, to: string, costCenters?: string) => {
    const seq = ++summarySeq.current;
    setSummaryLoading(true);
    setSummaryError(null);
    try {
      const data = await quillApi.costs.getSummary({ from, to, cost_centers: costCenters });
      if (mountedRef.current && seq === summarySeq.current) setSummary(data);
    } catch (e) {
      if (mountedRef.current && seq === summarySeq.current)
        setSummaryError(e instanceof Error ? e.message : 'Failed to load cost summary.');
    } finally {
      if (mountedRef.current && seq === summarySeq.current) setSummaryLoading(false);
    }
  }, []);

  const loadDaily = useCallback(
    async (from: string, to: string, costCenters: string | undefined, provider: string) => {
      const seq = ++dailySeq.current;
      setDailyLoading(true);
      setDailyError(null);
      try {
        const data = await quillApi.costs.getDaily({
          from,
          to,
          cost_centers: costCenters,
          provider: provider || undefined,
        });
        if (mountedRef.current && seq === dailySeq.current) setDaily(data);
      } catch (e) {
        if (mountedRef.current && seq === dailySeq.current)
          setDailyError(e instanceof Error ? e.message : 'Failed to load daily spend.');
      } finally {
        if (mountedRef.current && seq === dailySeq.current) setDailyLoading(false);
      }
    },
    []
  );

  const loadPipelines = useCallback(async (from: string, to: string, costCenters?: string) => {
    const seq = ++pipelinesSeq.current;
    setPipelinesLoading(true);
    setPipelinesError(null);
    try {
      const data = await quillApi.costs.getPipelines({ from, to, cost_centers: costCenters });
      if (mountedRef.current && seq === pipelinesSeq.current) setPipelines(data);
    } catch (e) {
      if (mountedRef.current && seq === pipelinesSeq.current)
        setPipelinesError(e instanceof Error ? e.message : 'Failed to load pipeline spend.');
    } finally {
      if (mountedRef.current && seq === pipelinesSeq.current) setPipelinesLoading(false);
    }
  }, []);

  const loadMembers = useCallback(async (from: string, to: string, costCenters?: string) => {
    const seq = ++membersSeq.current;
    setMembersLoading(true);
    setMembersError(null);
    try {
      const data = await quillApi.costs.getMembers({ from, to, cost_centers: costCenters, limit: 10 });
      if (mountedRef.current && seq === membersSeq.current) setMembers(data);
    } catch (e) {
      if (mountedRef.current && seq === membersSeq.current)
        setMembersError(e instanceof Error ? e.message : 'Failed to load member spend.');
    } finally {
      if (mountedRef.current && seq === membersSeq.current) setMembersLoading(false);
    }
  }, []);

  // Time + key filters re-fetch the whole page. `providerFilter` is read here
  // (not in the dep array) so this cycle fetches the daily series with the
  // current provider; the provider-only effect below handles later toggles.
  const providerFilterRef = useRef(providerFilter);
  providerFilterRef.current = providerFilter;
  useEffect(() => {
    Promise.allSettled([
      loadSummary(range.from, range.to, costCentersParam),
      loadDaily(range.from, range.to, costCentersParam, providerFilterRef.current),
      loadPipelines(range.from, range.to, costCentersParam),
      loadMembers(range.from, range.to, costCentersParam),
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range.from, range.to, costCentersParam]);

  // Provider filter re-fetches ONLY the daily series (server-filtered) and
  // re-filters the pipeline table client-side. Skips the initial mount so the
  // daily series isn't double-fetched alongside the main effect (D6).
  const providerMountRef = useRef(true);
  useEffect(() => {
    if (providerMountRef.current) {
      providerMountRef.current = false;
      return;
    }
    loadDaily(range.from, range.to, costCentersParam, providerFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerFilter]);

  const filteredPipelines = useMemo(() => {
    return pipelines.filter((p) => {
      if (providerFilter && (p.provider ?? '').toLowerCase() !== providerFilter.toLowerCase()) return false;
      return true;
    });
  }, [pipelines, providerFilter]);

  const totalSpend = num(summary?.totals?.est_cost_usd);
  const byProvider = summary?.by_provider ?? [];
  const xaiSpend = num(byProvider.find((p) => p.provider.toLowerCase() === 'xai')?.est_cost_usd);
  const anthropicSpend = num(byProvider.find((p) => p.provider.toLowerCase() === 'anthropic')?.est_cost_usd);
  const byCostCenter = summary?.by_cost_center ?? [];

  // D10: derive the chip list from the response's cost centers, unioned with the
  // static COST_CENTERS (ordering hint) and any currently-selected key (so a
  // selected chip never disappears). A newly metered center appears automatically.
  const costCenterOptions = useMemo(() => {
    const seen = new Set<string>();
    const out: string[] = [];
    const push = (cc?: string | null) => {
      if (cc && !seen.has(cc)) {
        seen.add(cc);
        out.push(cc);
      }
    };
    COST_CENTERS.forEach(push);
    byCostCenter.forEach((r) => push(r.cost_center));
    selectedKeys.forEach(push);
    return out;
  }, [byCostCenter, selectedKeys]);

  // D10: "keys with spend" counts cost centers with actual spend, respecting the
  // active key filter (client-side, so it matches what the chips scope to).
  const keysWithSpend = useMemo(() => {
    const withSpend = byCostCenter.filter((r) => num(r.est_cost_usd) > 0);
    if (selectedKeys.size === 0) return withSpend.length;
    return withSpend.filter((r) => selectedKeys.has(r.cost_center)).length;
  }, [byCostCenter, selectedKeys]);

  const hasAnyData =
    totalSpend > 0 ||
    daily.some((d) => num(d.est_cost_usd) > 0) ||
    pipelines.length > 0 ||
    members.length > 0;

  const anyLoading = summaryLoading || dailyLoading || pipelinesLoading || membersLoading;
  const anyError = Boolean(summaryError || dailyError || pipelinesError || membersError);

  const goToPipeline = () => navigate('/dashboard/quill/pipeline-models');

  const pipelineColumns: Column<CostPipelineRow>[] = [
    {
      id: 'pipeline_key',
      label: 'Pipeline',
      render: (row) => (
        <Box>
          <MuiLink
            component="button"
            type="button"
            onClick={goToPipeline}
            sx={{
              fontFamily: typography.fonts.mono,
              fontSize: typography.sizes.sm,
              fontWeight: 600,
              textAlign: 'left',
              wordBreak: 'break-all',
            }}
          >
            {row.pipeline_key}
          </MuiLink>
          {row.cost_center && (
            <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block' }}>
              {row.cost_center}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      id: 'provider',
      label: 'Provider',
      render: (row) =>
        row.provider ? <Chip label={providerLabel(row.provider)} size="small" variant="outlined" /> : '—',
    },
    { id: 'calls', label: 'Calls', align: 'right', render: (row) => num(row.calls).toLocaleString() },
    {
      id: 'tokens',
      label: 'Tokens (in / out)',
      align: 'right',
      render: (row) => `${num(row.input_tokens).toLocaleString()} / ${num(row.output_tokens).toLocaleString()}`,
    },
    { id: 'est_cost_usd', label: 'Est. cost', align: 'right', render: (row) => formatCost(row.est_cost_usd) },
    {
      id: 'avg_latency_ms',
      label: 'Avg latency',
      align: 'right',
      render: (row) => (row.avg_latency_ms == null ? '—' : `${Math.round(num(row.avg_latency_ms))} ms`),
    },
    {
      id: 'error_rate',
      label: 'Error rate',
      align: 'right',
      render: (row) =>
        row.error_rate == null ? (
          '—'
        ) : (
          <Chip
            label={`${(num(row.error_rate) * 100).toFixed(1)}%`}
            size="small"
            color={num(row.error_rate) > 0.05 ? 'error' : 'default'}
            variant={num(row.error_rate) > 0.05 ? 'filled' : 'outlined'}
          />
        ),
    },
  ];

  const memberColumns: Column<CostMemberRow>[] = [
    {
      id: 'member_id',
      label: 'Member',
      render: (row) => (
        <Typography sx={{ fontFamily: typography.fonts.mono, fontSize: typography.sizes.sm, wordBreak: 'break-all' }}>
          {row.member_id}
        </Typography>
      ),
    },
    { id: 'calls', label: 'Calls', align: 'right', render: (row) => num(row.calls).toLocaleString() },
    { id: 'est_cost_usd', label: 'Spend', align: 'right', render: (row) => formatCost(row.est_cost_usd) },
  ];

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: '12px',
            backgroundColor: `${colors.costs.main}1A`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mr: 2,
            flexShrink: 0,
          }}
        >
          <PaidIcon sx={{ fontSize: 28, color: colors.costs.main }} />
        </Box>
        <Box>
          <Typography sx={{ fontFamily: typography.fonts.display, fontSize: '1.75rem', fontWeight: 700, color: colors.gray[900] }}>
            AI Costs
          </Typography>
          <Typography sx={{ fontFamily: typography.fonts.body, fontSize: typography.sizes.sm, color: 'text.secondary' }}>
            Estimated Quill engine spend by provider, cost center (key), pipeline, and member.
          </Typography>
        </Box>
      </Box>

      {/* Connection state (reads the health hook — no hardcoded environment). */}
      <Typography
        variant="caption"
        sx={{ display: 'block', mb: 3, color: status === 'up' ? 'success.main' : 'text.disabled' }}
      >
        ● {status === 'up' ? 'Connected' : status}
      </Typography>

      {/* Filters */}
      <Box
        sx={{
          border: `1px solid ${colors.gray[200]}`,
          borderRadius: '16px',
          p: 2,
          mb: 3,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        {/* Time preset */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: colors.gray[600], minWidth: 70 }}>
            Time range
          </Typography>
          <TimeRangeFilter value={timeRange} onChange={setTimeRange} presets={COST_PRESETS} />
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {range.from} → {range.to}
          </Typography>
        </Box>

        {/* Key filter (chips derived from the response — D10) */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: colors.gray[600], minWidth: 70 }}>
            Keys
          </Typography>
          <Chip
            label="All"
            size="small"
            onClick={clearKeys}
            color={selectedKeys.size === 0 ? 'primary' : 'default'}
            variant={selectedKeys.size === 0 ? 'filled' : 'outlined'}
            sx={{ fontWeight: 600 }}
          />
          {costCenterOptions.map((cc) => {
            const on = selectedKeys.has(cc);
            return (
              <Chip
                key={cc}
                label={cc}
                size="small"
                onClick={() => toggleKey(cc)}
                color={on ? 'primary' : 'default'}
                variant={on ? 'filled' : 'outlined'}
                sx={{ fontFamily: typography.fonts.mono }}
              />
            );
          })}
          {selectedKeys.size > 0 && (
            <Button size="small" variant="text" onClick={clearKeys} sx={{ textTransform: 'none' }}>
              Clear
            </Button>
          )}
        </Box>
      </Box>

      {summaryError && (
        <Box sx={{ mb: 3 }}>
          <SectionError
            message={summaryError}
            onRetry={() => loadSummary(range.from, range.to, costCentersParam)}
          />
        </Box>
      )}

      {/* Summary MetricCards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard title="Total spend" value={formatCost(totalSpend)} color="costs" icon="money" loading={summaryLoading} subtitle={`${range.from} → ${range.to}`} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard title="xAI" value={formatCost(xaiSpend)} color="revenue" loading={summaryLoading} subtitle="Grok family" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard title="Anthropic" value={formatCost(anthropicSpend)} color="users" loading={summaryLoading} subtitle="Claude family" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard title="Keys with spend" value={String(keysWithSpend)} color="content" loading={summaryLoading} subtitle={selectedKeys.size ? `${selectedKeys.size} selected` : 'all keys'} />
        </Grid>
      </Grid>

      {!anyLoading && !anyError && !hasAnyData ? (
        <EmptyState
          icon="money"
          title="No spend recorded yet"
          description="No spend in this range for the selected keys. Widen the time range, clear the key filter, or wait for the rewired Quill engine to log more runs."
        />
      ) : (
        <>
          {/* Per-key breakdown — the headline */}
          <Box sx={{ mb: 4 }}>
            <Typography sx={{ fontFamily: typography.fonts.heading, fontWeight: 600, fontSize: typography.sizes.lg, mb: 1.5 }}>
              Cost by key
            </Typography>
            {summaryError ? (
              <SectionError
                message={summaryError}
                onRetry={() => loadSummary(range.from, range.to, costCentersParam)}
              />
            ) : (
              <CostByKeyBreakdown rows={byCostCenter} totalSpend={totalSpend} loading={summaryLoading} />
            )}
          </Box>

          {/* Daily trend */}
          <Box sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5, flexWrap: 'wrap' }}>
              <Typography sx={{ fontFamily: typography.fonts.heading, fontWeight: 600, fontSize: typography.sizes.lg }}>
                Daily spend
              </Typography>
              <Box sx={{ flexGrow: 1 }} />
              <ToggleButtonGroup
                size="small"
                exclusive
                value={providerFilter}
                onChange={(_e, v) => setProviderFilter((v as string) ?? '')}
                aria-label="Provider filter"
              >
                <ToggleButton value="" sx={{ textTransform: 'none' }}>
                  All
                </ToggleButton>
                <ToggleButton value="xai" sx={{ textTransform: 'none' }}>
                  xAI
                </ToggleButton>
                <ToggleButton value="anthropic" sx={{ textTransform: 'none' }}>
                  Anthropic
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
            {dailyError ? (
              <SectionError
                message={dailyError}
                onRetry={() => loadDaily(range.from, range.to, costCentersParam, providerFilter)}
              />
            ) : (
              <CostTrendChart points={daily} />
            )}
          </Box>

          {/* Per-pipeline table */}
          <Box sx={{ mb: 4 }}>
            <Typography sx={{ fontFamily: typography.fonts.heading, fontWeight: 600, fontSize: typography.sizes.lg, mb: 2 }}>
              By pipeline
            </Typography>
            {pipelinesError ? (
              <SectionError
                message={pipelinesError}
                onRetry={() => loadPipelines(range.from, range.to, costCentersParam)}
              />
            ) : (
              <DataTable<CostPipelineRow>
                columns={pipelineColumns}
                data={filteredPipelines}
                loading={pipelinesLoading}
                emptyMessage="No pipeline spend in this range"
              />
            )}
          </Box>

          {/* Top members */}
          <Box sx={{ mb: 4 }}>
            <Typography sx={{ fontFamily: typography.fonts.heading, fontWeight: 600, fontSize: typography.sizes.lg, mb: 2 }}>
              Top members by spend
            </Typography>
            {membersError ? (
              <SectionError
                message={membersError}
                onRetry={() => loadMembers(range.from, range.to, costCentersParam)}
              />
            ) : (
              <DataTable<CostMemberRow>
                columns={memberColumns}
                data={members}
                loading={membersLoading}
                emptyMessage="No member spend in this range"
              />
            )}
          </Box>
        </>
      )}

      {/* Per-run drill-down — read-only lookup over GET /costs/runs (quick-win C) */}
      <Box sx={{ mb: 2 }}>
        <CostRunDrilldown />
      </Box>
    </Box>
  );
};

export default AICostsPage;
